import React, { useEffect, useState } from 'react';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { initializeApp, getApp, getApps } from 'firebase/app';

import { useNavigate, useLocation } from 'react-router-dom';
import './AjoutMembre.css';

type Props = { isAdmin: boolean };

function AjoutMembreLoge({ isAdmin }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const [photo, setPhoto] = useState<File | null>(null);
  const [photoURL, setPhotoURL] = useState<string>('');

  // ✅ loge verrouillée via URL ?loge=
  const [lockedLoge, setLockedLoge] = useState<string>('');

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    mail: '',
    adresse: '',
    codePostal: '',
    ville: '',
    telephone: '',
    loge: '',
    grades: '',
    fonction: '',

    administrateur: 'non',
    statut: 'Valide',
  });

  const [error, setError] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const [fonctions, setFonctions] = useState<string[]>([]);
  const [statuts, setStatuts] = useState<string[]>([]);
  const [loges, setLoges] = useState<string[]>([]);
  const [grades, setGrades] = useState<string[]>([]);

  const [showExistingMemberModal, setShowExistingMemberModal] = useState(false);
  const [existingMemberData, setExistingMemberData] = useState<any>(null);

  // ✅ Slug NOM/PRENOM propre pour ID Firestore
  const slugify = (s: string) =>
    s
      .trim()
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^A-Z0-9-]/g, '');

  /**
   * ✅ IMPORTANT:
   * On crée une "Firebase App" secondaire (même config que l'app principale),
   * puis on récupère son Auth.
   * Comme ça, createUserWithEmailAndPassword() NE change PAS la session admin.
   */
  const getSecondaryAuth = () => {
    const SECONDARY_APP_NAME = 'secondary-auth';

    // App principale déjà initialisée ailleurs (ton firebase.ts / main)
    const defaultApp = getApp();

    // Si l'app secondaire existe déjà, on la réutilise
    const existing = getApps().find((a) => a.name === SECONDARY_APP_NAME);
    const secondaryApp = existing || initializeApp(defaultApp.options, SECONDARY_APP_NAME);

    return getAuth(secondaryApp);
  };

  // ✅ Lire ?loge=... et verrouiller la loge (et la pré-remplir)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const logeParam = params.get('loge');

    if (logeParam) {
      const decoded = decodeURIComponent(logeParam);
      setLockedLoge(decoded);
      setFormData((p) => ({ ...p, loge: decoded }));
    } else {
      setLockedLoge('');
    }
  }, [location.search]);

  useEffect(() => {
    const fetchData = async () => {
      const db = getFirestore();

      const fonctionsSnapshot = await getDocs(collection(db, 'Fonctions'));
      setFonctions(fonctionsSnapshot.docs.map((d) => d.id));

      const gradesSnapshot = await getDocs(collection(db, 'Grades'));
      setGrades(gradesSnapshot.docs.map((d) => d.id));

      const statutsSnapshot = await getDocs(collection(db, 'Statut'));
      setStatuts(statutsSnapshot.docs.map((d) => d.id));

      const logesSnapshot = await getDocs(collection(db, 'Loges'));
      setLoges(logesSnapshot.docs.map((d) => d.id));
    };

    fetchData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    if (name === 'loge' && lockedLoge) return;

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhoto(file);

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') setPhotoURL(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhotoIfNeeded = async (existingPhoto?: string) => {
    if (!photo) return existingPhoto || 'Aucune photo';
    const storage = getStorage();
    const timestamp = Date.now();
    const photoRef = ref(storage, `membres/${timestamp}_${photo.name}`);
    await uploadBytes(photoRef, photo);
    return await getDownloadURL(photoRef);
  };

  // ✅ Créer user Auth via Auth secondaire (ne déconnecte pas l'admin)
  const createFirebaseAuthUserSecondary = async (email: string): Promise<string | null> => {
    try {
      const secondaryAuth = getSecondaryAuth();
      const cred = await createUserWithEmailAndPassword(secondaryAuth, email, '123456');
      const uid = cred.user.uid;

      // ✅ optionnel: on "nettoie" l'auth secondaire
      await secondaryAuth.signOut();

      return uid;
    } catch (err: any) {
      if (err?.code === 'auth/email-already-in-use') {
        setAuthError(
          "Cet email existe déjà dans Firebase Auth. " +
          "Si le membre n'existe pas dans Firestore, il faudra le créer/relier manuellement."
        );
        return null;
      }
      setAuthError(err?.message || 'Erreur Auth');
      return null;
    }
  };

  // ✅ Vérif mail -> modal
  const handleEmailBlur = async () => {
    const email = formData.mail.trim().toLowerCase();
    if (!email) return;

    try {
      const db = getFirestore();
      const q = query(collection(db, 'Membres'), where('mail', '==', email));
      const snap = await getDocs(q);

      if (!snap.empty) {
        setExistingMemberData(snap.docs[0].data());
        setShowExistingMemberModal(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ✅ Charger infos existantes, forcer choix fonction
  const loadExistingMemberData = () => {
    if (!existingMemberData) return;

    setFormData(() => ({
      nom: existingMemberData.nom || '',
      prenom: existingMemberData.prenom || '',
      dateNaissance: existingMemberData.dateNaissance || '',
      mail: existingMemberData.mail || '',
      adresse: existingMemberData.adresse || '',
      codePostal: existingMemberData.codePostal || '',
      ville: existingMemberData.ville || '',
      telephone: existingMemberData.telephone || '',
      loge: lockedLoge ? lockedLoge : '',
      grades: existingMemberData.grades || '',
      fonction: '',

      administrateur: isAdmin ? (existingMemberData.administrateur || 'non') : 'non',
      statut: isAdmin ? (existingMemberData.statut || 'Valide') : 'Valide',
    }));

    if (existingMemberData.photo) setPhotoURL(existingMemberData.photo);

    setShowExistingMemberModal(false);
  };

  const continueWithNewMember = () => {
    setShowExistingMemberModal(false);
    setExistingMemberData(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = formData.mail.trim().toLowerCase();

    if (!formData.nom || !formData.prenom || !email || !formData.loge || !formData.fonction) {
      setError('Veuillez remplir tous les champs obligatoires (nom, prénom, email, loge, fonction).');
      return;
    }

    const adminValue = isAdmin ? (formData.administrateur || 'non') : 'non';
    const statutValue = isAdmin ? (formData.statut || 'Valide') : 'Valide';

    try {
      setLoading(true);
      setError('');
      setAuthError('');

      const db = getFirestore();

      // ✅ membre existe déjà dans Firestore ?
      const q = query(collection(db, 'Membres'), where('mail', '==', email));
      const snap = await getDocs(q);

      if (!snap.empty) {
        // ✅ EXISTANT -> update ciblé
        const d = snap.docs[0];
        const membreRef = doc(db, 'Membres', d.id);
        const existingData = d.data();
        const photoUrlToSave = await uploadPhotoIfNeeded(existingData.photo);

        await updateDoc(membreRef, {
          photo: photoUrlToSave,

          loges: arrayUnion(formData.loge),
          [`fonctionsParLoge.${formData.loge}`]: formData.fonction,

          nom: formData.nom,
          prenom: formData.prenom,
          adresse: formData.adresse,
          codePostal: formData.codePostal,
          ville: formData.ville,
          telephone: formData.telephone,
          dateNaissance: formData.dateNaissance,
          grades: formData.grades,

          administrateur: adminValue,
          statut: statutValue,

          dateModification: serverTimestamp(),
        });

        navigate('/mapage/ma-loge');
        return;
      }

      // ✅ NOUVEAU MEMBRE : créer Auth SANS déconnecter l'admin
      const uid = await createFirebaseAuthUserSecondary(email);
      if (!uid) return;

      const photoUrlToSave = await uploadPhotoIfNeeded('Aucune photo');

      // ✅ ID Firestore = NOM-PRENOM-UID
      const membreId = `${slugify(formData.nom)}-${slugify(formData.prenom)}-${uid}`;

      await setDoc(doc(db, 'Membres', membreId), {
        id: membreId,
        uid,

        photo: photoUrlToSave,

        nom: formData.nom,
        prenom: formData.prenom,
        adresse: formData.adresse,
        codePostal: formData.codePostal,
        ville: formData.ville,
        telephone: formData.telephone,
        dateNaissance: formData.dateNaissance,
        mail: email,
        grades: formData.grades,

        loges: [formData.loge],
        fonctionsParLoge: { [formData.loge]: formData.fonction },

        administrateur: adminValue,
        statut: statutValue,

        dateCreation: serverTimestamp(),
        dateModification: serverTimestamp(),
      });

      navigate('/mapage/ma-loge');
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue lors de l'ajout du membre à la loge.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ajout-membre-container">
      <h2>Ajouter un membre à la loge</h2>

      {error && <div className="error-message">{error}</div>}
      {authError && <div className="error-message">{authError}</div>}

      {showExistingMemberModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚠️ Membre déjà existant</h3>
            <p className="modal-message">
              Un membre avec cette adresse email existe déjà. Voulez-vous charger ses infos et l’ajouter à une nouvelle loge ?
            </p>

            <div className="existing-member-info">
              <p><strong>Nom:</strong> {existingMemberData?.nom} {existingMemberData?.prenom}</p>
              <p><strong>Loge(s):</strong> {Array.isArray(existingMemberData?.loges) ? existingMemberData.loges.join(', ') : existingMemberData?.loge}</p>
              <p><strong>Grade:</strong> {existingMemberData?.grades}</p>
            </div>

            <div className="modal-buttons">
              <button type="button" onClick={loadExistingMemberData} className="btn-yes">
                Oui, charger
              </button>
              <button type="button" onClick={continueWithNewMember} className="btn-no">
                Non, nouveau membre
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="ajout-membre-form">
        <div className="form-columns">
          <div className="form-column">
            <div className="form-group">
              <label htmlFor="photo">Photo de profil:</label>
              <input type="file" id="photo" accept="image/*" onChange={handlePhotoChange} className="form-input" />
              {photoURL && (
                <div className="photo-preview">
                  <img src={photoURL} alt="Aperçu" />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="nom">Nom: <span className="required">*</span></label>
              <input type="text" id="nom" name="nom" value={formData.nom} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="prenom">Prénom: <span className="required">*</span></label>
              <input type="text" id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="mail">Email: <span className="required">*</span></label>
              <input type="email" id="mail" name="mail" value={formData.mail} onChange={handleChange} onBlur={handleEmailBlur} required />
            </div>

            <div className="form-group">
              <label htmlFor="telephone">Téléphone:</label>
              <input type="tel" id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="dateNaissance">Date de naissance:</label>
              <input type="date" id="dateNaissance" name="dateNaissance" value={formData.dateNaissance} onChange={handleChange} />
            </div>
          </div>

          <div className="form-column">
            <div className="form-group">
              <label htmlFor="adresse">Adresse:</label>
              <input type="text" id="adresse" name="adresse" value={formData.adresse} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="codePostal">Code Postal:</label>
              <input type="text" id="codePostal" name="codePostal" value={formData.codePostal} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="ville">Ville:</label>
              <input type="text" id="ville" name="ville" value={formData.ville} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="loge">Loge: <span className="required">*</span></label>
              <select
                id="loge"
                name="loge"
                value={formData.loge}
                onChange={handleChange}
                required
                disabled={!!lockedLoge}
                title={lockedLoge ? 'Loge verrouillée depuis Ma Loge' : ''}
              >
                <option value="">Sélectionnez une loge</option>
                {loges.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              {lockedLoge && (
                <small style={{ display: 'block', marginTop: 6 }}>
                  Loge verrouillée : <strong>{lockedLoge}</strong>
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="grades">Grade:</label>
              <select id="grades" name="grades" value={formData.grades} onChange={handleChange}>
                <option value="">Sélectionnez un grade</option>
                {grades.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fonction">Fonction pour cette loge: <span className="required">*</span></label>
              <select id="fonction" name="fonction" value={formData.fonction} onChange={handleChange} required>
                <option value="">Sélectionnez une fonction</option>
                {fonctions.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <>
                <div className="form-group">
                  <label htmlFor="administrateur">Administrateur:</label>
                  <select
                    id="administrateur"
                    name="administrateur"
                    value={formData.administrateur}
                    onChange={handleChange}
                    required
                  >
                    <option value="non">Non</option>
                    <option value="oui">Oui</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="statut">Statut:</label>
                  <select
                    id="statut"
                    name="statut"
                    value={formData.statut}
                    onChange={handleChange}
                    required
                  >
                    {statuts.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/mapage/ma-loge')} className="cancel-btn" disabled={loading}>
            Annuler
          </button>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Ajout en cours...' : 'Ajouter à la loge'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AjoutMembreLoge;
